
import { useState, useCallback, memo, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';
import { TabView, TabPanel } from 'primereact/tabview';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
// Eliminado next-auth en entorno SPA. Usar AuthContext y servicios locales.
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

// Obtener App IDs desde env o configuración global inyectada
const getFacebookAppId = () => {
    const fromEnv = process.env.REACT_APP_FACEBOOK_APP_ID;
    const fromWindow = typeof window !== 'undefined' && window.__APP_CONFIG__?.FACEBOOK_APP_ID;
    return fromEnv || fromWindow || '';
};

const getGoogleClientId = () => {
    const fromEnv = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const fromWindow = typeof window !== 'undefined' && window.__APP_CONFIG__?.GOOGLE_CLIENT_ID;
    return fromEnv || fromWindow || '';
};

const getFacebookScope = () => {
    const fromEnv = process.env.REACT_APP_FACEBOOK_SCOPE;
    const fromWindow = typeof window !== 'undefined' && window.__APP_CONFIG__?.FACEBOOK_SCOPE;
    return fromEnv || fromWindow || 'public_profile';
};

// Componente separado para el formulario de login
const LoginFormComponent = memo(({ loginData, loading, handleLogin, handleLoginDataChange }) => (
    <form onSubmit={handleLogin} className="flex flex-column gap-4">
        <div className="field">
            <label htmlFor="login-email" className="block text-900 font-medium mb-2">Email o Teléfono</label>
            <InputText id="login-email" type="text" placeholder="ejemplo@correo.com o +51 999 999 999" className="w-full" value={loginData.email} onChange={(e) => handleLoginDataChange('email', e.target.value)} disabled={loading} />
        </div>
        
        <div className="field">
            <label htmlFor="login-password" className="block text-900 font-medium mb-2">Contraseña</label>
            <Password id="login-password" placeholder="Tu contraseña" className="w-full" inputClassName="w-full" value={loginData.password} onChange={(e) => handleLoginDataChange('password', e.target.value)} feedback={false} disabled={loading} />
        </div>
        
        <div className="flex align-items-center justify-content-between mb-4">
            <div className="flex align-items-center">
                <Checkbox id="remember" checked={loginData.rememberMe} onChange={(e) => handleLoginDataChange('rememberMe', e.checked)} disabled={loading} binary />
                <label htmlFor="remember" className="ml-2 text-600">Recordarme</label>
            </div>
            <Button label="¿Olvidaste tu contraseña?" className="p-button-link p-button-sm" disabled={loading} />
        </div>
        
        <Button type="submit" label={loading ? 'Iniciando sesión...' : 'Iniciar Sesión'} icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-sign-in'} className="p-button-lg" disabled={loading} />
    </form>
));

// Componente separado para el formulario de registro
const RegisterFormComponent = memo(({ registerData, loading, handleRegister, handleRegisterDataChange }) => (
    <form onSubmit={handleRegister} className="flex flex-column gap-4">
        <div className="grid">
            <div className="col-6">
                <label htmlFor="reg-firstName" className="block text-900 font-medium mb-2">Nombre *</label>
                <InputText id="reg-firstName" placeholder="Tu nombre" className="w-full" value={registerData.firstName} onChange={(e) => handleRegisterDataChange('firstName', e.target.value)} disabled={loading} />
            </div>
            <div className="col-6">
                <label htmlFor="reg-lastName" className="block text-900 font-medium mb-2">Apellido *</label>
                <InputText id="reg-lastName" placeholder="Tu apellido" className="w-full" value={registerData.lastName} onChange={(e) => handleRegisterDataChange('lastName', e.target.value)} disabled={loading} />
            </div>
        </div>
        
        <div className="field">
            <label htmlFor="reg-email" className="block text-900 font-medium mb-2">Email *</label>
            <InputText id="reg-email" type="email" placeholder="ejemplo@correo.com" className="w-full" value={registerData.email} onChange={(e) => handleRegisterDataChange('email', e.target.value)} disabled={loading} />
        </div>
        
        <div className="field">
            <label htmlFor="reg-phone" className="block text-900 font-medium mb-2">Teléfono</label>
            <InputText id="reg-phone" type="tel" placeholder="+51 999 999 999" className="w-full" value={registerData.phone} onChange={(e) => handleRegisterDataChange('phone', e.target.value)} disabled={loading} />
        </div>
        
        <div className="field">
            <label htmlFor="reg-password" className="block text-900 font-medium mb-2">Contraseña *</label>
            <Password id="reg-password" placeholder="Mínimo 8 caracteres" className="w-full" inputClassName="w-full" value={registerData.password} onChange={(e) => handleRegisterDataChange('password', e.target.value)} disabled={loading} />
        </div>
        
        <div className="field">
            <label htmlFor="reg-confirm-password" className="block text-900 font-medium mb-2">Confirmar Contraseña *</label>
            <Password id="reg-confirm-password" placeholder="Repite tu contraseña" className="w-full" inputClassName="w-full" value={registerData.confirmPassword} onChange={(e) => handleRegisterDataChange('confirmPassword', e.target.value)} feedback={false} disabled={loading} />
        </div>
        
        <div className="flex align-items-start mb-4">
            <Checkbox id="reg-terms" checked={registerData.acceptTerms} onChange={(e) => handleRegisterDataChange('acceptTerms', e.checked)} disabled={loading} binary />
            <label htmlFor="reg-terms" className="ml-2 text-600 line-height-3">
                Acepto los <Link to="/terms" target="_blank" rel="noopener noreferrer" className="p-button-link p-0">Términos y Condiciones</Link> y la <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="p-button-link p-0">Política de Privacidad</Link>
            </label>
        </div>
        
        <Button type="submit" label={loading ? 'Creando cuenta...' : 'Crear Cuenta'} icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-user-plus'} className="p-button-lg" disabled={loading} />
    </form>
));

export default function AuthModal({ visible, onHide, onAuthSuccess }) {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { login, register, socialLogin } = useAuth();
    
    // Estados para login
    const [loginData, setLoginData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });
    
    // Estados para registro
    const [registerData, setRegisterData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        acceptTerms: false
    });

    const handleLogin = useCallback(async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        try {
            // Validaciones básicas
            if (!loginData.email || !loginData.password) {
                throw new Error('Por favor completa todos los campos');
            }
            
            // Usar AuthContext para login
            const result = await login({ email: loginData.email, password: loginData.password });
            if (result?.success) {
                toast.success('Inicio de sesión exitoso');
                onAuthSuccess(result.data || {});
                onHide();
            } else {
                throw new Error(result?.error || 'Error al iniciar sesión');
            }
            
        } catch (err) {
            setError(err.message || 'Error al iniciar sesión');
        } finally {
            setLoading(false);
        }
    }, [loginData, onAuthSuccess]);

    const handleRegister = useCallback(async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        try {
            // Validaciones
            if (!registerData.firstName || !registerData.lastName || !registerData.email || !registerData.password) {
                throw new Error('Por favor completa todos los campos obligatorios');
            }
            
            if (registerData.password !== registerData.confirmPassword) {
                throw new Error('Las contraseñas no coinciden');
            }
            
            if (!registerData.acceptTerms) {
                throw new Error('Debes aceptar los términos y condiciones');
            }
            
            // Usar AuthContext para registro
            const result = await register({
                nombre: registerData.firstName,
                apellido: registerData.lastName,
                email: registerData.email,
                telefono: registerData.phone || '',
                contrasenia: registerData.password
            });
            if (result?.success) {
                toast.success('Usuario registrado exitosamente');
                onAuthSuccess(result.data || {});
                onHide();
            } else {
                throw new Error(result?.error || 'Error al registrarse');
            }
            
        } catch (err) {
            setError(err.message || 'Error al registrarse');
        } finally {
            setLoading(false);
        }
    }, [registerData, onAuthSuccess]);

    // Handlers optimizados para evitar re-renderizados
    const handleLoginDataChange = useCallback((field, value) => {
        setLoginData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleRegisterDataChange = useCallback((field, value) => {
        setRegisterData(prev => ({ ...prev, [field]: value }));
    }, []);

    // Cargar Google Identity cuando el modal esté visible
    useEffect(() => {
        if (!visible) return;
        if (typeof window !== 'undefined' && !window.google) {
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        }
    }, [visible]);

    // Cargar Facebook SDK cuando el modal esté visible
    useEffect(() => {
        if (!visible) return;
        const fbAppId = getFacebookAppId();
        if (typeof window !== 'undefined' && !window.FB) {
            const fbScript = document.createElement('script');
            fbScript.src = 'https://connect.facebook.net/es_LA/sdk.js';
            fbScript.async = true;
            fbScript.defer = true;
            fbScript.crossOrigin = 'anonymous';
            fbScript.onload = () => {
                try {
                    if (window.FB && fbAppId) {
                        window.FB.init({
                            appId: fbAppId,
                            cookie: true,
                            xfbml: false,
                            version: 'v17.0',
                        });
                    }
                } catch (e) {
                    // Evitar romper la UX si init falla
                }
            };
            document.head.appendChild(fbScript);
        } else if (typeof window !== 'undefined' && window.FB && fbAppId) {
            try {
                window.FB.init({ appId: fbAppId, cookie: true, xfbml: false, version: 'v17.0' });
            } catch (e) {
                // Evitar romper la UX si init falla
            }
        }
    }, [visible]);

    const handleGoogleLogin = useCallback(() => {
        setLoading(true);
        setError('');
        try {
            const clientId = getGoogleClientId();
            if (!clientId) {
                throw new Error('Google Client ID no configurado');
            }

            const callback = async (response) => {
                const idToken = response?.credential;
                if (!idToken) {
                    setLoading(false);
                    return;
                }
                try {
                    const result = await socialLogin('Google', idToken);
                    if (result?.success) {
                        onAuthSuccess(result);
                        onHide();
                    } else {
                        throw new Error(result?.error || 'Error en login con Google');
                    }
                } catch (err) {
                    setError(err.message || 'Error en login con Google');
                    toast.error(err.message || 'Error en login con Google');
                } finally {
                    setLoading(false);
                }
            };

            if (window.google?.accounts?.id) {
                // Opt-in FedCM y flujo popup como fallback
                window.google.accounts.id.initialize({ client_id: clientId, callback, ux_mode: 'popup', use_fedcm_for_prompt: true });
                // Renderizar botón como fallback visual si el prompt no aparece
                const btnContainer = document.getElementById('googleButtonDiv');
                if (btnContainer) {
                    try {
                        window.google.accounts.id.renderButton(btnContainer, { theme: 'outline', size: 'large', type: 'standard', text: 'continue_with' });
                    } catch (_) {}
                }
                // Solicitar prompt sin usar métodos de momento obsoletos
                try { window.google.accounts.id.prompt(); } catch (_) {}
                // Liberar estado de carga para evitar quedarse en "Procesando..."
                setLoading(false);
            } else {
                throw new Error('Google Identity no disponible');
            }
        } catch (err) {
            setError(err.message || 'Error al iniciar con Google');
            toast.error(err.message || 'Error al iniciar con Google');
            setLoading(false);
        }
    }, [socialLogin, onAuthSuccess, onHide]);

    const handleFacebookLogin = useCallback(() => {
        setLoading(true);
        setError('');
        try {
            const appId = getFacebookAppId();
            if (!appId) {
                throw new Error('Facebook App ID no configurado');
            }
            if (typeof window === 'undefined' || !window.FB) {
                throw new Error('Facebook SDK no disponible');
            }
            const fbResponseHandler = async (response) => {
                if (response && response.authResponse) {
                    const accessToken = response.authResponse.accessToken;
                    try {
                        const result = await socialLogin('Facebook', accessToken);
                        if (result?.success) {
                            toast.success('Inicio de sesión con Facebook exitoso');
                            onAuthSuccess(result);
                            onHide();
                        } else {
                            throw new Error(result?.error || 'Error en login con Facebook');
                        }
                    } catch (err) {
                        setError(err.message || 'Error en login con Facebook');
                        toast.error(err.message || 'Error en login con Facebook');
                    } finally {
                        setLoading(false);
                    }
                } else {
                    // Usuario canceló o no autorizó
                    setLoading(false);
                }
            };

            window.FB.login(function(response) {
                // No pasar una función async directamente al SDK de FB
                fbResponseHandler(response);
            }, { scope: getFacebookScope() });
        } catch (err) {
            setError(err.message || 'Error al iniciar con Facebook');
            toast.error(err.message || 'Error al iniciar con Facebook');
            setLoading(false);
        }
    }, [socialLogin, onAuthSuccess, onHide]);

    const SocialButtons = useCallback(() => (
        <div className="grid mb-4">
            <div className="col-12 md:col-6">
                <Button className="p-button-outlined w-full" onClick={handleGoogleLogin} disabled={loading}>
                    <i className="pi pi-google text-red-500 mr-2 text-4xl"></i>
                    <span className='font-bold'>Continuar con Google</span>
                </Button>
                <div id="googleButtonDiv" className="mt-2"></div>
            </div>
            <div className="col-12 md:col-6">
                <Button className="p-button-outlined w-full" onClick={handleFacebookLogin} disabled={loading}>
                    <i className="pi pi-facebook text-indigo-500 mr-2 text-4xl"></i>
                    <span className='font-bold'>Continuar con Facebook</span>
                </Button>
            </div>
        </div>
    ), [loading, handleGoogleLogin]);



    const resetForms = () => {
        setLoginData({ email: '', password: '', rememberMe: false });
        setRegisterData({ firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '', acceptTerms: false });
        setError('');
        setActiveTab(0);
    };

    const handleHide = () => {
        if (!loading) {
            resetForms();
            onHide();
            try {
                // Limpieza defensiva de máscara de Dialog en caso de quedar residual
                const masks = document.querySelectorAll('.p-dialog-mask');
                masks.forEach((m) => {
                    if (m && m.parentNode) m.parentNode.removeChild(m);
                });
                document.body.classList.remove('p-overflow-hidden');
                document.body.style.overflow = '';
            } catch (_) {
                // Evitar romper si el DOM no está disponible
            }
        }
    };

    return (
        <Dialog 
            header="Iniciar Sesión" 
            visible={visible} 
            onHide={handleHide} 
            modal 
            dismissableMask 
            appendTo={typeof document !== 'undefined' ? document.body : undefined}
            className="w-full md:w-30rem" 
            closable={!loading} 
            blockScroll 
            pt={{ mask: { className: 'bg-black-alpha-60' } }}
        >
            <div className="text-center mb-4">
                <i className="pi pi-shopping-cart text-4xl text-primary mb-3"></i>
                <h4 className="text-900 font-semibold mb-2">¡Casi listo para tu compra!</h4>
                <p className="text-600 m-0">Inicia sesión o crea una cuenta para continuar con tu pedido</p>
            </div>
            
            {error && (
                <Message severity="error" text={error} className="w-full mb-4" />
            )}
            
            <SocialButtons />
            
            <Divider align="center">
                <span className="text-600 text-sm">O continúa con email</span>
            </Divider>
            
            <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)} className="mt-4">
                <TabPanel header="Iniciar Sesión">
                    {activeTab === 0 && (
                        <LoginFormComponent 
                            loginData={loginData}
                            loading={loading}
                            handleLogin={handleLogin}
                            handleLoginDataChange={handleLoginDataChange}
                        />
                    )}
                </TabPanel>
                <TabPanel header="Crear Cuenta">
                    {activeTab === 1 && (
                        <RegisterFormComponent 
                            registerData={registerData}
                            loading={loading}
                            handleRegister={handleRegister}
                            handleRegisterDataChange={handleRegisterDataChange}
                        />
                    )}
                </TabPanel>
            </TabView>
            
            {loading && (
                <div className="flex align-items-center justify-content-center mt-4">
                    <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" />
                    <span className="ml-2 text-600">Procesando...</span>
                </div>
            )}
        </Dialog>
    );
}
